import { atom, Getter, Setter } from "jotai";
import {
  GetRemittanceUseCase,
  GetRemittanceWithIncompleteDeductionsUseCase,
  ExportRemittanceDocumentsUseCase,
} from "@/core/usecases/remittance";
import { BuildDeductionFetchParamsUseCase, SendRemittanceForApprovalUseCase, CheckRemittanceForApprovalUseCase } from "@/core/usecases/deduction";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { DateAdapter } from "@/infrastructure/adapters/dayJs";
import { RemittanceRepository } from "@/infrastructure/repositories/remittance";
import { DeductionRepository } from "@/infrastructure/repositories/deduction";
import {
  currentPageAtom,
  pageLimitAtom,
  remittanceAtom,
  currentExpandedRemittanceKeyAtom,
  sortColumnAtom,
} from "./remittance.atoms";
import { ITableSortColumn } from "@/core/interface";
import { searchRemittanceAtom } from "../main/main.atoms";
import {
  filterInfoAtom,
  isFilterAppliedAtom,
  QuickFIlterInfo,
  quickFilterInfoAtom,
} from "../deductionFilter";
import { AppConfig } from "@/infrastructure/services/config";
import { IRemittanceRepositoryResponse } from "@/core/repositories/remittance";
import { workspaceSettingsAtom } from "@/store/workspace";
import { refreshDeductionWorkspaceAtom } from "../main";
import { getDeductionsAtom } from "../deduction/deduction.actions";
import { drawerInfoAtom, isDrawerOpenAtom } from "../drawer";
import { Deduction } from "@/core/entities/deduction";
import { createLogger } from "@/infrastructure/services/logger";

//GraphQL client
const client = GraphQlAdapter.getInstance().getClient();
//Remittance repository
const remittanceRepository = new RemittanceRepository(client);
const deductionRepository = new DeductionRepository(client);
const dateAdapter = new DateAdapter();
const logger = createLogger("remittance.actions");

const resolveExpandedRemittanceKey = (
  remittances: IRemittanceRepositoryResponse["remittances"],
  search: string,
  statusId: QuickFIlterInfo["statusId"],
  currentExpandedRemittanceKey: number | null
): number | null => {
  if (search !== "" && remittances.length > 0 && statusId !== "incomplete") {
    return remittances[0].key;
  }

  if (currentExpandedRemittanceKey == null) {
    return null;
  }

  return remittances.some(remittance => remittance.key === currentExpandedRemittanceKey)
    ? currentExpandedRemittanceKey
    : null;
};

const updateRemittanceState = (
  set: Setter,
  get: Getter,
  response: IRemittanceRepositoryResponse | null,
  search: string
) => {
  const quickFilterInfo = get(quickFilterInfoAtom);
  const remittances = response?.remittances ?? [];
  const currentState = get(remittanceAtom);

  set(remittanceAtom, {
    ...currentState,
    remittances,
    totalCount: response?.totalCount ?? 0,
    currentPage: get(currentPageAtom),
    pageLimit: get(pageLimitAtom),
    isLoading: false,
    error: null,
    currentExpandedRemittanceKey: resolveExpandedRemittanceKey(
      remittances,
      search,
      quickFilterInfo.statusId,
      currentState.currentExpandedRemittanceKey
    ),
  });
};

const handleError = (set: Setter, get: Getter, error: unknown) => {
  set(remittanceAtom, {
    ...get(remittanceAtom),
    remittances: [],
    totalCount: 0,
    isLoading: false,
    error: error as Error,
  });
};

// Action atoms
export const getRemittancesAtom = atom(null, async (get, set, refetch?: boolean) => {
  set(remittanceAtom, {
    ...get(remittanceAtom),
    isLoading: true,
  });

  try {
    const search = get(searchRemittanceAtom);
    const filterInfo = get(filterInfoAtom);
    const isFilterApplied = get(isFilterAppliedAtom);
    const quickFilterInfo = get(quickFilterInfoAtom);
    const workspaceSettings = get(workspaceSettingsAtom);
    const appConfig = AppConfig.getInstance();
    const defaultCheckDateRangeInMonths =
      workspaceSettings?.defaultFilterCheckDateRange ?? appConfig.DEFAULT_FILTER_CHECK_DATE_RANGE;
    const sortColumn = get(sortColumnAtom);

    const baseParams = {
      page_number: get(currentPageAtom),
      page_size: get(pageLimitAtom),
      sort_column: sortColumn?.sortColumnKey,
      sort_order: sortColumn?.order,
    };

    let remittancesResponse: IRemittanceRepositoryResponse | null = null;

    if (quickFilterInfo.statusId === "incomplete") {
      remittancesResponse = await new GetRemittanceWithIncompleteDeductionsUseCase(remittanceRepository).execute(
        baseParams,
        refetch
      );
    } else if (quickFilterInfo.statusId === "all") {
      const isNoDefaultDateRange = defaultCheckDateRangeInMonths === -1;
      const shouldSkipDefaultDateForAppliedEmptyCheckDateFilter =
        isFilterApplied &&
        filterInfo.checkDateRange === null &&
        filterInfo.checkFromDate === null &&
        filterInfo.checkToDate === null;
      const shouldOmitDefaultCheckDateRange = isNoDefaultDateRange || shouldSkipDefaultDateForAppliedEmptyCheckDateFilter;
      const today = dateAdapter.getCurrentDate().toISOString();
      const defaultCheckFromDate = shouldOmitDefaultCheckDateRange
        ? appConfig.FILTER_DATE_CONFIG.DEFAULT_FROM_DATE
        : dateAdapter.format(dateAdapter.subtract(today, defaultCheckDateRangeInMonths, "months").toISOString(), "YYYY-MM-DD");
      const filterParams = new BuildDeductionFetchParamsUseCase().execute({
        filterInfo,
        statusId: quickFilterInfo.statusId,
        search,
        config: {
          defaultCheckFromDate,
          defaultCheckToDate: appConfig.FILTER_DATE_CONFIG.DEFAULT_TO_DATE,
          isNoDefaultDateRange: shouldOmitDefaultCheckDateRange,
        },
        includeSearchFields: true,
        includeStatusIds: true,
      });

      remittancesResponse = await new GetRemittanceUseCase(remittanceRepository).execute(
        {
          ...baseParams,
          ...filterParams,
        },
        refetch
      );
    }

    updateRemittanceState(set, get, remittancesResponse, search);
  } catch (error: unknown) {
    handleError(set, get, error);
  }
});

//Update column sort
export const setSortColumnAtom = atom(null, async (get, set, sortColumn: ITableSortColumn | null) => {
  set(remittanceAtom, {
    ...get(remittanceAtom),
    sortColumn: sortColumn,
    currentPage: 1,
    currentExpandedRemittanceKey: null,
  });
});

//Update current page
export const setCurrentPageAtom = atom(null, async (get, set, page: number) => {
  set(remittanceAtom, {
    ...get(remittanceAtom),
    currentPage: page,
    currentExpandedRemittanceKey: null,
  });
});

//Update current expanded remittance key
export const setCurrentExpandedRemittanceKeyAtom = atom(null, async (get, set, key: number) => {
  if (key === get(currentExpandedRemittanceKeyAtom)) {
    set(remittanceAtom, {
      ...get(remittanceAtom),
      currentExpandedRemittanceKey: null,
    });
  } else {
    set(remittanceAtom, {
      ...get(remittanceAtom),
      currentExpandedRemittanceKey: key,
    });
  }
});

//Download remittance documents
export const downloadRemittanceDocumentsAtom = atom(
  null,
  async (
    _get,
    _set,
    remittanceId: number,
    checkNumber: string,
    formats: { date?: string; number?: string }
  ) => {
    const response = await new ExportRemittanceDocumentsUseCase(remittanceRepository).execute(
      {
        remittance_id: remittanceId,
        check_number: checkNumber,
      },
      formats
    );
    return response;
  }
);

export type CheckRemittanceForApprovalResult =
  | { status: "eligible" }
  | { status: "ineligible"; message: string }
  | { status: "error" };

export type SendRemittanceForApprovalResult =
  | { status: "success"; deductions: Deduction[] }
  | { status: "error" };

const NO_APPLICABLE_DEDUCTIONS_MESSAGE = "No applicable deductions found for approval";

export const checkRemittanceForApprovalAtom = atom(
  null,
  async (get, set, remittanceId: number): Promise<CheckRemittanceForApprovalResult> => {
    set(remittanceAtom, {
      ...get(remittanceAtom),
      isRemittanceApprovalChecking: true,
    });

    try {
      const { checkRemittanceForApproval } = await new CheckRemittanceForApprovalUseCase(deductionRepository).execute({
        remittance_id: remittanceId,
        comment: "",
        action: "APPROVE",
      });

      set(remittanceAtom, {
        ...get(remittanceAtom),
        isRemittanceApprovalChecking: false,
      });

      if (!checkRemittanceForApproval.hasApplicable) {
        return {
          status: "ineligible",
          message: checkRemittanceForApproval.message || NO_APPLICABLE_DEDUCTIONS_MESSAGE,
        };
      }

      return { status: "eligible" };
    } catch (error: unknown) {
      logger.error("checkRemittanceForApprovalAtom error", error);
      set(remittanceAtom, {
        ...get(remittanceAtom),
        isRemittanceApprovalChecking: false,
      });
      return { status: "error" };
    }
  }
);

export const sendRemittanceForApprovalAtom = atom(
  null,
  async (get, set, remittanceId: number, comment: string): Promise<SendRemittanceForApprovalResult> => {
    set(remittanceAtom, {
      ...get(remittanceAtom),
      isRemittanceApprovalSending: true,
    });

    try {
      const currentPage = get(currentPageAtom);
      const { sendRemittanceForApproval } = await new SendRemittanceForApprovalUseCase(deductionRepository).execute(
        {
          remittance_id: remittanceId,
          comment,
          action: "APPROVE",
        },
        currentPage
      );

      set(remittanceAtom, {
        ...get(remittanceAtom),
        isRemittanceApprovalSending: false,
      });

      if (sendRemittanceForApproval.length > 0) {
        await set(refreshDeductionWorkspaceAtom, true);

        const isDrawerOpen = get(isDrawerOpenAtom);
        const drawerInfo = get(drawerInfoAtom);
        const drawerRemittanceId = drawerInfo?.remittanceItem?.remittanceId;

        if (isDrawerOpen && drawerRemittanceId === remittanceId) {
          await set(getDeductionsAtom, true);
        }
      }

      return { status: "success", deductions: sendRemittanceForApproval };
    } catch (error: unknown) {
      logger.error("sendRemittanceForApprovalAtom error", error);
      set(remittanceAtom, {
        ...get(remittanceAtom),
        isRemittanceApprovalSending: false,
      });
      return { status: "error" };
    }
  }
);
